import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { getSessionId } from '../../helpers/storageUtils';
import { DatosGenerales } from '../../Models/datosGenerales';
import { bodyException } from '../../Models/exceptionResponse';
import { COTIZACION_PAUSA_INACTIVO, YATEMENOSDATOS } from '../../routes.constans';
import { selectData } from '../../storage/Lead/data.selectors';
import { ExceptionService } from '../excepciones/exception.service';
import { buildFullUrl } from './inactivity.utils';

type ValidKeys = 'PolicyHolder' | 'Tenant' | 'Property' | 'ProductCode' | string;
type AllowedKeyCombinations = ValidKeys[];

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private inactivityTimer: any;
  private readonly INACTIVITY_TIMEOUT = environment.FIRST_INACTIVITY_TIME || environment.LOCAL_TIMER || 300000;
  private readonly INACTIVITY_TIMEOUT_SECOND = environment.SECOND_INACTIVITY_TIME || 60000;

  private isSecondInactivity: boolean = false;
  private redirectRoute: string = COTIZACION_PAUSA_INACTIVO;

  private onTimeoutCallback?: () => void;
  private beforeRedirectCallback?: () => void;

  private keys: AllowedKeyCombinations = [];
  private eventListeners: (() => void)[] = [];

  private estadoRescate: 'Habilitado' | 'Inhabilitado' = 'Inhabilitado';

  private originalScreenName: string = '';
  private originalUrl: string = '';
  private originalLeadRequirement: string = '';
  private originalScreenPercentage: string = '';

  private screenPercentage: string = '';
  private leadRequirement: string = '';
  private screenName: string = '';
  optionalProducts: object | undefined;

  // Data from NgRx Store
  private leadData: DatosGenerales | null = null;

  private router = inject(Router);
  private exceptionService = inject(ExceptionService);
  private store = inject(Store);

  constructor() {
    // Suscribirse al store para obtener datos del lead
    this.store.select(selectData).subscribe(data => {
      this.leadData = data;
    });
  }

  startInactivityTimer(
    redirectRoute?: string,
    onTimeout?: () => void,
    keys?: AllowedKeyCombinations,
    screenPercentage?: string,
    leadRequirement?: string,
    optionalProducts?: object,
    useSecondTimer?: boolean,
    beforeRedirect?: () => void,
    screenName?: string
  ): void {
    if (redirectRoute) { this.redirectRoute = redirectRoute; }
    if (onTimeout) { this.onTimeoutCallback = onTimeout; }
    if (keys) { this.keys = keys; }
    if (screenPercentage) {
      this.screenPercentage = screenPercentage;
      if (!useSecondTimer) {
        this.originalScreenPercentage = screenPercentage;
      }
    }
    if (leadRequirement) {
      this.leadRequirement = leadRequirement;
      if (!useSecondTimer) {
        this.originalLeadRequirement = leadRequirement;
      }
    }
    if (optionalProducts) { this.optionalProducts = optionalProducts; }
    if (useSecondTimer !== undefined) { this.isSecondInactivity = useSecondTimer; }
    if (beforeRedirect) { this.beforeRedirectCallback = beforeRedirect; }

    if (screenName) {
      this.screenName = screenName;
      if (!useSecondTimer) {
        this.originalScreenName = screenName;

        // Todas las URLs de retoma apuntan al cotizador
        // buildFullUrl ya contiene la base correcta según ambiente
        this.originalUrl = buildFullUrl(this.router.url, ['recordId']);
      }
    }

    this.setupTimer(keys);
    this.setupEventListeners();
  }

  public startDefaultTimerForScreen(
    screenName: string,
    leadRequirement: string,
    screenPercentage: string,
    beforeRedirect?: () => void
  ): void {
    this.startInactivityTimer(
      COTIZACION_PAUSA_INACTIVO,
      undefined,
      undefined,
      screenPercentage,
      leadRequirement,
      undefined,
      false,
      beforeRedirect,
      screenName
    );
  }

  stopTimer(): void {
    this.clearTimer();
    this.removeEventListeners();
  }

  setLeadData(data: DatosGenerales | null): void {
    this.leadData = data;
  }

  getLeadData(): DatosGenerales | null {
    return this.leadData;
  }

  private setupTimer(keys?: AllowedKeyCombinations): void {
    const timeout = this.isSecondInactivity ? this.INACTIVITY_TIMEOUT_SECOND : this.INACTIVITY_TIMEOUT;

    this.inactivityTimer = setTimeout(() => {
      this.handleTimeout(keys);
    }, timeout);
  }

  private clearTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private handleTimeout(keys: AllowedKeyCombinations = []): void {
    if (this.beforeRedirectCallback) {
      this.beforeRedirectCallback();
    }

    if (this.onTimeoutCallback) {
      this.onTimeoutCallback();
    }

    // On first inactivity, just redirect to the pause screen.
    if (!this.isSecondInactivity) {
      this.router.navigate([this.redirectRoute]);
      return;
    }

    // On second inactivity, call the exception service and then redirect.
    const sessionData = this.getLeadData();
    const sessionId = getSessionId().toString();
    const leadId = sessionData?.LeadResponse?.data?.Id || '';

    console.log('🔍 Debug - Datos del lead obtenidos del Store:', {
      sessionData: sessionData,
      mobile_phone: sessionData?.Lead?.mobile_phone,
      name: sessionData?.Lead?.name,
      plate: sessionData?.Lead?.plate,
      leadId: leadId
    });

    // Construir el payload completo con todos los datos (igual que en autos)
    const exceptionPayload: bodyException = {
      required_status: this.originalLeadRequirement,
      percentege_lead: Number(this.originalScreenPercentage),
      mobile_phone: sessionData?.Lead?.mobile_phone || '',
      product: 'Motos Digital',
      name: sessionData?.Lead?.name || '',
      plate: sessionData?.Lead?.plate || '',
      estadoRetoma: 'Habilitado',
      estadoRescate: 'Inhabilitado',
      pantalla: this.originalScreenName,
      UrlRetoma: this.originalUrl,
      session_id: sessionId,
      cityName: sessionData?.Lead?.cityName || '',
      circulation_zone_id: sessionData?.Lead?.circulation_zone_id || '',
    };

    // Determinar si debe enviar lead_id (NO enviarlo en la primera pantalla - Cotizador)
    const isFirstScreen = this.originalScreenName === 'Cotizador';
    const leadIdToSend = isFirstScreen ? '' : leadId;

    console.log(`📋 Pantalla: ${this.originalScreenName}, Es primera pantalla: ${isFirstScreen}, Lead ID a enviar: "${leadIdToSend}"`);

    // Enviar excepción usando sendGeneralException con el payload completo
    this.exceptionService.sendGeneralException(
      sessionId,
      leadIdToSend,
      exceptionPayload
    ).subscribe({
      next: (response) => {
        console.log('✅ Excepción de inactividad enviada exitosamente:', response);
      },
      error: (error) => {
        console.error('❌ Error al enviar excepción de inactividad:', error);
      },
    });

    // Redirect to a final page.
    this.router.navigate([YATEMENOSDATOS]);
  }

  private setupEventListeners(): void {
    this.removeEventListeners();

    const resetTimer = () => {
      this.clearTimer();
      this.setupTimer(this.keys);
    };

    const events = ['mousemove', 'keypress', 'click', 'wheel'];
    events.forEach(eventType => {
      const listener = resetTimer.bind(this);
      document.addEventListener(eventType, listener);
      this.eventListeners.push(() => document.removeEventListener(eventType, listener));
    });
  }

  private removeEventListeners(): void {
    this.eventListeners.forEach(removeListener => removeListener());
    this.eventListeners = [];
  }

  enableEstadoRescate(): void { this.estadoRescate = 'Habilitado'; }
  disableEstadoRescate(): void { this.estadoRescate = 'Inhabilitado'; }
  getOriginalUrl(): string { return this.originalUrl; }
  getInactivityKeys(): AllowedKeyCombinations { return this.keys; }
  getOriginalScreenName(): string { return this.originalScreenName; }
  getOriginalLeadRequirement(): string { return this.originalLeadRequirement; }
  getOriginalScreenPercentage(): string { return this.originalScreenPercentage; }
}
